import { useEffect, useState } from 'react';
import { Users, Bell } from 'lucide-react';
import api from '../../utils/api';

/**
 * DealReachPreview — live counter shown next to the "Add to Batch"
 * button on the retailer's Post Deal screen.
 *
 * Fetches ``/api/retailer/analytics/deal-reach`` on mount and shows:
 *   - reachable_dacs      total DACs whose DACSAI contains this DRLP
 *   - with_push_tokens    subset who will receive a push notification
 *   - push_opt_in_rate    percentage
 *
 * Silently renders nothing if the endpoint 401s or the DRLP has zero
 * DACs in range (no reason to show "0 customers" — that would be
 * discouraging on a brand-new pilot store).
 */
export default function DealReachPreview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/retailer/analytics/deal-reach');
        if (alive) setData(res.data);
      } catch (e) {
        // Best-effort — this widget is decorative. Silent fail.
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!data || data.reachable_dacs === 0) return null;

  return (
    <div
      className="mb-3 flex items-center justify-between rounded-lg border border-emerald-100 bg-emerald-50/60 px-3 py-2 text-xs"
      data-testid="deal-reach-preview"
    >
      <div className="flex items-center gap-2">
        <Users className="h-3.5 w-3.5 text-emerald-600" />
        <span className="text-slate-700">
          This deal will reach{' '}
          <strong data-testid="deal-reach-reachable">{data.reachable_dacs}</strong>{' '}
          customer{data.reachable_dacs === 1 ? '' : 's'} in your DACDRLP-List
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <Bell className="h-3 w-3 text-emerald-600" />
        <span className="text-slate-600">
          <strong data-testid="deal-reach-with-push">{data.with_push_tokens}</strong>
          {' '}with push
          {' '}
          <span className="text-slate-400">
            ({data.push_opt_in_rate}%)
          </span>
        </span>
      </div>
    </div>
  );
}
